import { userAvatarBucketName } from '@/config/env';
import { useUIStore } from '@/config/stores/ui-store';
import type { Conversation } from '@/features/conversation/models/conversation';
import { GradientAvatar } from '@/shared/components';
import { formatDateCompact } from '@/shared/utilities';
import { Badge, Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface ConversationItemProps {
  conversation: Conversation;
  isSelected?: boolean;
  onClick?: (conversation: Conversation) => void;
}

export default function ConversationItem({ conversation, isSelected = false, onClick }: ConversationItemProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { sidebarExpanded } = useUIStore();

  const handleClick = () => {
    onClick?.(conversation);
  };

  const [getAvatarRef, bucketName] = useMemo(() => {
    const bucketName = conversation.conversationType === 'DIRECT' ? userAvatarBucketName : undefined;
    return [conversation.conversationAvatarRef, bucketName];
  }, [conversation.conversationType, conversation.conversationAvatarRef, userAvatarBucketName]);

  const getLastMessageTime = () => {
    if (!conversation.updatedAt) return '';
    return formatDateCompact(conversation.updatedAt);
  };

  return sidebarExpanded ? (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: 1,
        cursor: 'pointer',
        position: 'relative',
        transition: theme.transitions.create(['background-color'], {
          duration: theme.transitions.duration.shortest
        }),
        '&:hover': {
          backgroundColor: 'action.hover'
        },
        ...(isSelected && {
          backgroundColor: 'action.active',
          '&:hover': {
            backgroundColor: 'action.hover'
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            height: '60%',
            width: 3,
            backgroundColor: 'primary.main',
            borderRadius: '0 4px 4px 0'
          }
        })
      }}
    >
      <Box position='relative'>
        <Badge
          color='primary'
          badgeContent={conversation.unreadCount}
          max={99}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          <GradientAvatar
            peerId={conversation.conversationId}
            variant={conversation.conversationType === 'GROUP' ? 'rounded' : 'circular'}
            name={conversation.conversationName}
            src={getAvatarRef}
            bucketName={bucketName}
          />
        </Badge>
      </Box>

      <Stack direction='column' spacing={0.5} flex={1} minWidth={0}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography
            variant='subtitle2'
            noWrap
            sx={{
              fontWeight: conversation.unreadCount > 0 ? 600 : 500,
              color: isSelected ? 'primary.main' : 'text.primary'
            }}
          >
            {conversation.conversationName}
          </Typography>
          <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0, ml: 1 }}>
            {getLastMessageTime()}
          </Typography>
        </Box>

        <Typography
          variant='body2'
          color='text.secondary'
          noWrap
          sx={{
            fontWeight: conversation.unreadCount > 0 ? 500 : 400,
            color: conversation.unreadCount > 0 ? 'text.primary' : 'text.secondary'
          }}
        >
          {conversation.lastMessageContent || t('conversations.list.lastMessageFallback')}
        </Typography>
      </Stack>
    </Box>
  ) : (
    <Tooltip
      title={conversation.conversationName}
      enterDelay={500}
      leaveDelay={200}
      enterTouchDelay={700}
      leaveTouchDelay={1500}
      disableInteractive={false}
      placement='right'
      arrow
    >
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 1,
          borderRadius: 1,
          cursor: 'pointer',
          transition: theme.transitions.create(['background-color'], {
            duration: theme.transitions.duration.shortest
          }),
          '&:hover': {
            backgroundColor: 'action.hover'
          },
          ...(isSelected && {
            backgroundColor: 'action.active',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          })
        }}
      >
        <Box position='relative'>
          <Badge
            color='primary'
            badgeContent={conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            <GradientAvatar
              peerId={conversation.conversationId}
              variant={conversation.conversationType === 'GROUP' ? 'rounded' : 'circular'}
              name={conversation.conversationName}
              src={getAvatarRef}
              bucketName={bucketName}
            />
          </Badge>
        </Box>
      </Box>
    </Tooltip>
  );
}
